import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Switch,
  ActivityIndicator,
  RefreshControl,
  Platform,
  TextInput,
} from 'react-native';
import { useFocusEffect, useRouter, useNavigation } from 'expo-router';
import { showAlert, showConfirm } from '../../../src/utils/alert';
import { getErrorMessage } from '../../../src/utils/errors';
import { openLegalUrl } from '../../../src/constants';
import { openAccountPage } from '../../../src/auth/accountPage';
import {
  authApi,
  userApi,
  settingsApi,
  subscriptionApi,
  exportApi,
  User,
  Settings,
  Subscription,
} from '../../../src/api/client';
import {
  offersEnterpriseEnquiry,
  planSourceFor,
  planTermFor,
} from '../../../src/utils/planDisplay';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { decryptExportData } from '../../../src/utils/exportDecrypt';
import { withStepUp, StepUpError } from '../../../src/auth/stepUp';
import type { StepUpReturnRoute } from '../../../src/auth/stepUp';
import { useAuthStore } from '../../../src/stores/authStore';
import { useNetworkStore } from '../../../src/stores/networkStore';
import { useAiSettingsStore } from '../../../src/stores/aiSettingsStore';
import { useDiagnosticsStore } from '../../../src/stores/diagnosticsStore';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';

/**
 * Where a web step-up sends the browser back to.
 *
 * A step-up is a full page navigation on web (src/auth/stepUp.ts); without this
 * the user returns to '/' and the root layout re-decides, landing them on the
 * activities tab with no sign that the thing they asked for never happened.
 * Only the route is persisted, never the pending action.
 */
const RETURN_TO_SETTINGS: StepUpReturnRoute = '/(tabs)/settings';

/** How an account came by its plan.
 *
 *  The first three are the only values written now. The rest are retired but
 *  still sitting on historical rows: migration 005 deliberately did not flatten
 *  them to 'admin_manual', on the same reasoning 004 gave for access codes —
 *  where a plan came from is a true fact about it, and rewriting that is
 *  unrecoverable in a way dropping a column is not. So they still need a label,
 *  and it has to be an honest one: calling a store purchase "Administrator"
 *  would be worse than saying nothing. */
const PLAN_SOURCE_LABELS: Record<string, string> = {
  admin_manual: 'Administrator',
  offline: 'Invoice',
  enterprise_group: 'Group membership',
  iap: 'App Store or Google Play',
  web: 'Web',
  access_code: 'Access code',
};

function planSourceLabel(source: string | null): string {
  if (!source) return 'Administrator';
  return PLAN_SOURCE_LABELS[source] ?? source;
}

const createStyles = (theme: Theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.bgSecondary,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 40,
  },
  centered: {
    flex: 1,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    backgroundColor: theme.colors.bgSecondary,
    paddingHorizontal: 24,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 15,
    color: theme.colors.textMuted,
  },
  errorIcon: {
    fontSize: 48,
    marginBottom: 12,
    color: theme.colors.warning,
  },
  errorText: {
    fontSize: 15,
    color: theme.colors.error,
    textAlign: 'center' as const,
    marginBottom: 16,
  },
  retryButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 8,
  },
  retryText: {
    color: theme.colors.background,
    fontWeight: '600' as const,
    fontSize: 15,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700' as const,
    color: theme.colors.textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 10,
    paddingHorizontal: 4,
  },
  card: {
    backgroundColor: theme.colors.background,
    borderRadius: 12,
    overflow: 'hidden' as const,
    ...theme.shadows.sm,
  },
  profileCard: {
    backgroundColor: theme.colors.background,
    borderRadius: 12,
    padding: 20,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 16,
    ...theme.shadows.sm,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.primaryBg,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
  },
  avatarText: {
    fontSize: 22,
    fontWeight: '700' as const,
    color: theme.colors.primary,
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: 18,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  profileEmail: {
    fontSize: 14,
    color: theme.colors.textMuted,
  },
  authMethod: {
    fontSize: 12,
    color: theme.colors.textDisabled,
    marginTop: 4,
  },
  subscriptionRow: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    padding: 16,
  },
  tierBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6,
  },
  tierBadgeText: {
    fontSize: 12,
    fontWeight: '800' as const,
    letterSpacing: 0.5,
  },
  switchRow: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    padding: 16,
  },
  switchLabelContainer: {
    flex: 1,
    marginRight: 12,
  },
  settingLabel: {
    fontSize: 15,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
  },
  settingDescription: {
    fontSize: 13,
    color: theme.colors.textDisabled,
    marginTop: 2,
    lineHeight: 18,
  },
  settingValue: {
    fontSize: 15,
    color: theme.colors.textMuted,
    fontWeight: '500' as const,
  },
  infoRow: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    padding: 16,
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.bgTertiary,
    marginHorizontal: 16,
  },
  actionRow: {
    padding: 16,
    alignItems: 'center' as const,
  },
  logoutText: {
    fontSize: 16,
    fontWeight: '600' as const,
    color: theme.colors.primary,
  },
  deleteText: {
    fontSize: 16,
    fontWeight: '600' as const,
    color: theme.colors.error,
  },
});

export default function SettingsScreen() {
  const router = useRouter();
  const navigation = useNavigation();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const logout = useAuthStore((state) => state.logout);
  const logoutAndForgetDevice = useAuthStore((state) => state.logoutAndForgetDevice);
  const accessToken = useAuthStore((state) => state.accessToken);
  const connectivityState = useNetworkStore((s) => s.connectivityState);

  const [user, setUser] = useState<User | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportProgress, setExportProgress] = useState<number | null>(null);
  // Web only: a finished export waiting for the user to press "Save Export
  // File". The press is the user gesture the browser needs — see handleSaveExport.
  const [exportReady, setExportReady] = useState<{ url: string; filename: string } | null>(null);
  const exportPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const exportJobIdRef = useRef<string | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState('');

  const fetchAll = useCallback(async (showRefresh = false) => {
    if (showRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const [userRes, settingsRes, subRes] = await Promise.allSettled([
        userApi.getMe(),
        settingsApi.get(),
        subscriptionApi.get(),
      ]);

      if (userRes.status === 'fulfilled') {
        setUser(userRes.value.data);
      }
      if (settingsRes.status === 'fulfilled') {
        setSettings(settingsRes.value.data);
        useAiSettingsStore.getState().loadFromSettings(settingsRes.value.data);
        useDiagnosticsStore.getState().loadFromSettings(settingsRes.value.data);
      }
      if (subRes.status === 'fulfilled') {
        setSubscription(subRes.value.data);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load settings.'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchAll();
    }, [fetchAll])
  );

  useEffect(() => {
    return () => {
      if (exportPollRef.current) clearInterval(exportPollRef.current);
    };
  }, []);

  const handleToggleSetting = async (
    key: keyof Settings,
    value: boolean
  ) => {
    if (!settings || isSaving) return;
    if (!requireConnectivity()) return;

    const prev = settings[key];
    setSettings({ ...settings, [key]: value });
    setIsSaving(true);

    // Diagnostics stops at the moment the user says stop, not when the PUT
    // returns. Turning reporting off is exactly the case where the round-trip
    // might fail, and continuing to send in the meantime is the one outcome the
    // switch must never produce. Reverted below if the save fails.
    if (key === 'diagnostics_opt_in') {
      useDiagnosticsStore.getState().setEnabled(value);
    }

    try {
      const response = await settingsApi.update({ [key]: value });
      setSettings(response.data);
      useDiagnosticsStore.getState().loadFromSettings(response.data);
    } catch {
      setSettings({ ...settings, [key]: prev });
      if (key === 'diagnostics_opt_in') {
        useDiagnosticsStore.getState().setEnabled(prev as boolean);
      }
      showAlert('Error', 'Failed to update setting.');
    } finally {
      setIsSaving(false);
    }
  };


  const handleEditProfile = () => {
    setEditName(user?.display_name || '');
    setIsEditingProfile(true);
  };

  const handleSaveProfile = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === user?.display_name) {
      setIsEditingProfile(false);
      return;
    }
    if (!requireConnectivity()) return;
    setIsSaving(true);
    try {
      const response = await userApi.updateMe({ display_name: trimmed });
      setUser(response.data);
      setIsEditingProfile(false);
    } catch {
      showAlert('Error', 'Failed to update profile.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    showConfirm(
      'Sign Out',
      'Are you sure you want to sign out?',
      () => logout(),
      { confirmText: 'Sign Out', destructive: true }
    );
  };

  const handleLogoutForget = () => {
    showConfirm(
      'Sign Out & Forget Device',
      'This will remove your encryption key from this device. You will need your recovery phrase to sign in again.',
      () => logoutAndForgetDevice(),
      { confirmText: 'Sign Out & Forget', destructive: true }
    );
  };

  const handleDeleteAccount = () => {
    if (!requireConnectivity()) return;
    // ⚠️  THIS DOES NOT DELETE THE A3RN IDENTITY, and the copy must not imply
    //     it does. `DELETE /user` removes the Cynosure row and CASCADEs its
    //     data; the account at auth.a3rn.com — email, name, passkey — survives
    //     untouched. Cynosure is a relying party and deliberately holds no IdP
    //     admin credential to delete anything with (identity-architecture §3),
    //     and the identity is shared with the rest of the A3RN fleet, so
    //     deleting it here would sign the user out of unrelated apps.
    //
    //     The consequence users actually hit: signing in again succeeds and
    //     JIT-provisions a BRAND NEW empty account (idp_service.py
    //     `provision_from_claims`). Copy that promised the account was gone
    //     made that read as the deletion having silently failed, so the second
    //     dialog says what will happen instead.
    showConfirm(
      'Delete My Cynosure Data',
      'This permanently deletes your Cynosure data — activities, goals, visions and teams. It cannot be undone.',
      () => {
        showConfirm(
          'Confirm Deletion',
          'Are you absolutely sure? This cannot be reversed. Your A3RN sign-in stays active and still works for other A3RN apps — signing in again will start you with a fresh, empty Cynosure account.',
          async () => {
            try {
              // Action first, step up only on the server's challenge — see
              // withStepUp. Calling stepUp() ahead of this redirects the page on
              // web, so the delete never ran and nothing reported a failure.
              await withStepUp(() => userApi.deleteAccount(), RETURN_TO_SETTINGS);
              await logoutAndForgetDevice();
            } catch (err) {
              // The browser is already leaving for the hosted login.
              if (err instanceof StepUpError && Platform.OS === 'web') return;
              showAlert('Error', 'Failed to delete account. Please try logging in again.');
            }
          },
          { confirmText: 'I understand, delete', destructive: true }
        );
      },
      { confirmText: 'Delete', destructive: true }
    );
  };

  const EXPORT_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

  const cleanupExportPoll = () => {
    if (exportPollRef.current) clearInterval(exportPollRef.current);
    exportPollRef.current = null;
    exportJobIdRef.current = null;
  };

  /**
   * Hand the finished export to the browser, from a press.
   *
   * The download used to be triggered straight from the polling code, where
   * the user's last gesture was the confirm dialog up to two minutes earlier.
   * iOS Safari will not start a download without a recent one, the anchor was
   * never attached to the document, and the object URL was revoked on the
   * very next line — WebKit begins downloads asynchronously and cancels one
   * whose URL is already gone. Then "Export Complete" was shown regardless,
   * and the user had no file. A button makes the press the gesture.
   */
  const handleSaveExport = () => {
    if (!exportReady) return;
    const { url, filename } = exportReady;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    setExportReady(null);
  };

  const handleExportData = () => {
    if (!requireConnectivity()) return;
    showConfirm(
      'Export My Data',
      'This will create a JSON export of all your data. It may take a moment.',
      async () => {
        try {
          setExportProgress(0);

          // ⚠️  NOT OPTIONAL, and ACTION FIRST. /user/export requires a step-up
          //     like the two destructive routes, so a failure here is the whole
          //     operation failing and the catch below reports it. It used to
          //     call stepUp() ahead of the create, which on web redirects the
          //     page — so the export was never requested and the user came back
          //     to settings with nothing to show for it. See src/auth/stepUp.ts.
          const createRes = await withStepUp(() => exportApi.create(), RETURN_TO_SETTINGS);
          const jobId = createRes.data.export_id;
          exportJobIdRef.current = jobId;

          // Poll for completion with timeout
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              cleanupExportPoll();
              reject(new Error('Export timed out. Please try again later.'));
            }, EXPORT_TIMEOUT_MS);

            exportPollRef.current = setInterval(async () => {
              try {
                const statusRes = await exportApi.getStatus(jobId);
                const { status: jobStatus, progress_percent } = statusRes.data;
                setExportProgress(progress_percent);

                if (jobStatus === 'completed') {
                  clearTimeout(timeout);
                  cleanupExportPoll();
                  resolve();
                } else if (jobStatus === 'failed') {
                  clearTimeout(timeout);
                  cleanupExportPoll();
                  reject(new Error('Export failed on the server.'));
                }
              } catch (err) {
                clearTimeout(timeout);
                cleanupExportPoll();
                reject(err);
              }
            }, 2000);
          });

          // Download and decrypt the export
          const jsonRes = await exportApi.download(jobId);
          const rawExport = typeof jsonRes.data === 'string'
            ? JSON.parse(jsonRes.data)
            : jsonRes.data;
          const cleanExport = await decryptExportData(rawExport);
          const text = JSON.stringify(cleanExport, null, 2);

          if (Platform.OS === 'web') {
            // Not downloaded from here — see handleSaveExport for why the
            // browser gets a button to press instead.
            const blob = new Blob([text], { type: 'application/json' });
            setExportReady({
              url: URL.createObjectURL(blob),
              filename: `cynosure-export-${jobId}.json`,
            });
          } else {
            const { Paths, File: ExpoFile } = require('expo-file-system/next');
            const filePath = `${Paths.cache}/cynosure-export.json`;
            const file = new ExpoFile(Paths.cache, 'cynosure-export.json');
            if (file.exists) file.delete();
            file.create();
            file.write(text);
            const { shareAsync: share } = require('expo-sharing');
            await share(filePath, {
              mimeType: 'application/json',
              dialogTitle: 'Save your data export',
            });
          }

          setExportProgress(null);
          if (Platform.OS === 'web') {
            showAlert('Export Ready', 'Your export is ready. Press "Save Export File" under Account to download it.');
          } else {
            showAlert('Export Complete', 'Your data has been exported successfully.');
          }
        } catch (err: unknown) {
          setExportProgress(null);
          // The browser is already leaving for the hosted login; an alert would
          // flash and vanish behind the navigation.
          if (err instanceof StepUpError && Platform.OS === 'web') return;
          showAlert('Export Failed', getErrorMessage(err, 'Could not export your data. Please try again.'));
        }
      },
      { confirmText: 'Export' }
    );
  };

  const tierColors: Record<string, { bg: string; text: string }> = {
    free: { bg: colors.bgTertiary, text: colors.textMuted },
    enterprise: { bg: colors.warningBg, text: colors.warningText },
  };

  if (isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Loading settings...</Text>
      </View>
    );
  }

  if (error) {
    // Settings is intentionally online-only (see docs/offline-resilience.md):
    // show a clearer message when staleness is due to lost connectivity.
    const offlineMessage =
      connectivityState === 'offline'
        ? 'Settings require an internet connection. Please reconnect and try again.'
        : connectivityState === 'server-down'
          ? 'The server is temporarily unavailable. Please try again in a moment.'
          : error;
    return (
      <View style={styles.centered}>
        <Text style={styles.errorIcon}>{'\u26A0'}</Text>
        <Text style={styles.errorText}>{offlineMessage}</Text>
        <TouchableOpacity
          style={styles.retryButton}
          onPress={() => fetchAll()}
          accessibilityLabel="Retry"
          accessibilityRole="button"
          testID="settings.retry-button"
        >
          <Text style={styles.retryText} accessible={false}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const tierStyle = tierColors[subscription?.tier || 'free'] || tierColors.free;

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      refreshControl={
        <RefreshControl
          refreshing={isRefreshing}
          onRefresh={() => fetchAll(true)}
          colors={[colors.primary]}
          tintColor={colors.primary}
        />
      }
    >
      {/* Profile Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Profile</Text>
        <View style={styles.profileCard}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>
              {user?.display_name?.[0]?.toUpperCase() ||
                user?.email?.[0]?.toUpperCase() ||
                'U'}
            </Text>
          </View>
          <View style={styles.profileInfo}>
            {isEditingProfile ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <TextInput
                  style={[styles.profileName, { flex: 1, borderBottomWidth: 1, borderBottomColor: colors.primary, paddingVertical: 4 }]}
                  value={editName}
                  onChangeText={setEditName}
                  autoFocus
                  returnKeyType="done"
                  onSubmitEditing={handleSaveProfile}
                  accessibilityLabel="Display name"
                  testID="settings.display-name-input"
                />
                <TouchableOpacity
                  onPress={handleSaveProfile}
                  disabled={isSaving}
                  accessibilityLabel="Save display name"
                  accessibilityRole="button"
                  testID="settings.save-profile-button"
                >
                  <Text style={{ color: colors.primary, fontWeight: '600' as const, fontSize: 14 }} accessible={false}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setIsEditingProfile(false)}
                  accessibilityLabel="Cancel editing"
                  accessibilityRole="button"
                  testID="settings.cancel-edit-button"
                >
                  <Text style={{ color: colors.textMuted, fontSize: 14 }} accessible={false}>Cancel</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                onPress={handleEditProfile}
                accessibilityLabel="Edit display name"
                accessibilityRole="button"
                testID="settings.edit-profile-button"
              >
                <Text style={styles.profileName}>
                  {user?.display_name || 'User'}
                  <Text style={{ color: colors.textMuted, fontSize: 13 }}> Edit</Text>
                </Text>
              </TouchableOpacity>
            )}
            <Text style={styles.profileEmail}>{user?.email || ''}</Text>
            <Text style={styles.authMethod}>Signed in with Email</Text>
          </View>
        </View>
      </View>

      {/* Plan Section
          There is nothing to buy from inside the app: Cynosure is free, and
          Enterprise is granted by an admin (docs/subscription.md). So this
          section reports what the account has; it never sells anything. */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Plan</Text>

        <View style={styles.card}>
          <View style={styles.subscriptionRow}>
            <Text style={styles.settingLabel}>Current Plan</Text>
            <View
              style={[styles.tierBadge, { backgroundColor: tierStyle.bg }]}
            >
              <Text style={[styles.tierBadgeText, { color: tierStyle.text }]}>
                {(subscription?.tier || 'free').toUpperCase()}
              </Text>
            </View>
          </View>
          {planSourceFor(subscription) && (
            <View style={styles.subscriptionRow}>
              <Text style={styles.settingLabel}>Granted via</Text>
              <Text style={styles.settingValue} testID="settings.plan-source">
                {planSourceLabel(planSourceFor(subscription))}
              </Text>
            </View>
          )}
          {/* Gated on the tier, not on the date alone — planDisplay.ts carries
              the why, and its tests are what hold the rule. */}
          {planTermFor(subscription) && (
            <View style={styles.subscriptionRow}>
              <Text style={styles.settingLabel}>Active Until</Text>
              <Text style={styles.settingValue} testID="settings.plan-ends-at">
                {new Date(planTermFor(subscription)!).toLocaleDateString()}
              </Text>
            </View>
          )}
          {offersEnterpriseEnquiry(subscription) && (
            <>
              <View style={styles.divider} />
              {/* Not an upgrade prompt: there is no checkout to send anyone to,
                  and this sells nothing. Enterprise is arranged offline and
                  provisioned by an admin, so the only honest affordance is the
                  way to start that conversation — the contact form, whose
                  Billing category is already described as "Invoices and
                  Enterprise plan questions". */}
              <TouchableOpacity
                style={styles.infoRow}
                onPress={() =>
                  (navigation as any).push('contact', { category: 'billing' })
                }
                accessibilityLabel="Contact us about an Enterprise plan"
                accessibilityRole="button"
                testID="settings.enterprise-enquiry-button"
              >
                <View style={styles.switchLabelContainer}>
                  <Text style={styles.settingLabel} accessible={false}>
                    Enterprise
                  </Text>
                  <Text style={styles.settingDescription} accessible={false}>
                    Centralised seat management for organisations. Commercial and
                    organisational use requires an Enterprise plan, arranged with
                    our team.
                  </Text>
                </View>
                <Text style={styles.logoutText} accessible={false}>
                  Contact Us
                </Text>
              </TouchableOpacity>
            </>
          )}
          {subscription?.tier === 'enterprise' && (
            <>
              <View style={styles.divider} />
              <TouchableOpacity
                style={styles.actionRow}
                onPress={() => (navigation as any).push('enterprise-group')}
                accessibilityLabel="Manage Enterprise Group"
                accessibilityRole="button"
                testID="settings.manage-enterprise-group-button"
              >
                <Text style={styles.logoutText} accessible={false}>Manage Enterprise Group</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>

      {/* Teams Section
          Teams are collaboration, not billing: any tier can own or join one,
          and the screen is E2EE team membership, not seats. It sat at the
          bottom of the Subscription card, which read as a paid add-on.
          "Manage Enterprise Group" stays there deliberately — that one really
          is subscription-scoped, gated on tier === 'enterprise' and about who
          occupies the plan's seats. */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Teams</Text>
        <View style={styles.card}>
          <TouchableOpacity
            style={styles.actionRow}
            onPress={() => (navigation as any).push('teams')}
            accessibilityLabel="Manage Teams"
            accessibilityRole="button"
            // Unchanged on purpose: both synthetic monitors assert this handle.
            testID="settings.manage-teams-button"
          >
            <Text style={styles.logoutText} accessible={false}>Manage Teams</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Privacy & Security Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Privacy & Security</Text>
        <View style={styles.card}>
          <View style={styles.infoRow}>
            <View style={styles.switchLabelContainer}>
              <Text style={styles.settingLabel}>End-to-End Encryption</Text>
              <Text style={styles.settingDescription}>
                Your data is encrypted on-device before being stored
              </Text>
            </View>
            <View style={[styles.tierBadge, { backgroundColor: colors.successBg }]}>
              <Text style={[styles.tierBadgeText, { color: colors.successText }]}>
                ACTIVE
              </Text>
            </View>
          </View>
          <View style={styles.divider} />
          <View style={styles.infoRow}>
            <View style={styles.switchLabelContainer}>
              <Text style={styles.settingLabel}>Recovery Phrase</Text>
              <Text style={styles.settingDescription}>
                Your recovery phrase was shown during setup
              </Text>
            </View>
          </View>
          <View style={styles.divider} />
          <TouchableOpacity
            style={styles.actionRow}
            onPress={() => router.push('/(setup)/transfer-source' as any)}
            accessibilityLabel="Transfer Key to New Device"
            accessibilityRole="button"
            testID="settings.transfer-key-to-new-device-button"
          >
            <Text style={styles.logoutText} accessible={false}>Transfer Key to New Device</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* The Passkeys and Security sections stood here. Both retired in Phase
          4: passkeys are enrolled and managed at the identity provider, whose
          console also lists sessions, and Cynosure no longer stores either. */}

      {/* Preferences Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Preferences</Text>
        <View style={styles.card}>
          <TouchableOpacity
            style={styles.actionRow}
            onPress={() => (navigation as any).push('personalize')}
            accessibilityLabel="Personalize"
            accessibilityRole="button"
            testID="settings.personalize-button"
          >
            <Text style={styles.logoutText} accessible={false}>Personalize</Text>
          </TouchableOpacity>
          <View style={styles.divider} />
          <TouchableOpacity
            style={styles.actionRow}
            onPress={() => (navigation as any).push('ai-features')}
            accessibilityLabel="AI Features"
            accessibilityRole="button"
            testID="settings.ai-features-button"
          >
            <Text style={styles.logoutText} accessible={false}>AI Features</Text>
          </TouchableOpacity>
          <View style={styles.divider} />
          <TouchableOpacity
            style={styles.actionRow}
            onPress={() => (navigation as any).push('notification-preferences')}
            accessibilityLabel="Notifications"
            accessibilityRole="button"
            testID="settings.notifications-button"
          >
            <Text style={styles.logoutText} accessible={false}>Notifications</Text>
          </TouchableOpacity>
          <View style={styles.divider} />
          {/* Was "Analytics — share anonymous usage data", which described
              something Cynosure does not collect: user actions, screen views
              and tracing are all explicitly disabled in the Faro config. What
              actually leaves the device is diagnostics, so the row now says
              that and genuinely gates it. Applies to every device on the
              account — the setting lives on the server, not this device. */}
          <View style={styles.switchRow}>
            <View style={styles.switchLabelContainer}>
              <Text style={styles.settingLabel}>Share Diagnostic Reports</Text>
              <Text style={styles.settingDescription}>
                Send crash and error reports to help fix problems. No usage
                tracking, and never your content. Applies to all your devices.
              </Text>
            </View>
            <Switch
              value={settings?.diagnostics_opt_in ?? true}
              onValueChange={(value) =>
                handleToggleSetting('diagnostics_opt_in', value)
              }
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={settings?.diagnostics_opt_in ?? true ? colors.primary : colors.bgSecondary}
              accessibilityLabel="Share Diagnostic Reports"
              accessibilityHint="Sends crash and error reports from all your devices to help fix problems"
              testID="settings.diagnostics-switch"
            />
          </View>
        </View>
      </View>

      {/* About Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>About</Text>
        <View style={styles.card}>
          <TouchableOpacity
            style={styles.actionRow}
            onPress={() => (navigation as any).push('about')}
            accessibilityLabel="About Cynosure"
            accessibilityRole="button"
            testID="settings.about-button"
          >
            <Text style={styles.logoutText} accessible={false}>About Cynosure</Text>
          </TouchableOpacity>
          <View style={styles.divider} />
          <TouchableOpacity
            style={styles.actionRow}
            onPress={() => (navigation as any).push('contact')}
            accessibilityLabel="Contact Us"
            accessibilityRole="button"
            testID="settings.contact-us-button"
          >
            <Text style={styles.logoutText} accessible={false}>Contact Us</Text>
          </TouchableOpacity>
          <View style={styles.divider} />
          <TouchableOpacity
            style={styles.actionRow}
            onPress={() => openLegalUrl('support')}
            accessibilityLabel="Support"
            accessibilityRole="link"
            testID="settings.support-button"
          >
            <Text style={styles.logoutText} accessible={false}>Support</Text>
          </TouchableOpacity>
          <View style={styles.divider} />
          <TouchableOpacity
            style={styles.actionRow}
            onPress={() => openLegalUrl('terms')}
            accessibilityLabel="Terms of Service"
            accessibilityRole="link"
            testID="settings.terms-of-service-button"
          >
            <Text style={styles.logoutText} accessible={false}>Terms of Service</Text>
          </TouchableOpacity>
          <View style={styles.divider} />
          <TouchableOpacity
            style={styles.actionRow}
            onPress={() => openLegalUrl('privacy')}
            accessibilityLabel="Privacy Policy"
            accessibilityRole="link"
            testID="settings.privacy-policy-button"
          >
            <Text style={styles.logoutText} accessible={false}>Privacy Policy</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Account Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Account</Text>
        <View style={styles.card}>
          {exportReady ? (
            <TouchableOpacity
              style={styles.actionRow}
              onPress={handleSaveExport}
              accessibilityLabel="Save export file"
              accessibilityRole="button"
              testID="settings.save-export-button"
            >
              <Text style={styles.logoutText} accessible={false}>Save Export File</Text>
            </TouchableOpacity>
          ) : exportProgress !== null ? (
            <View style={styles.actionRow}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
                <ActivityIndicator size="small" color={colors.primary} />
                <Text style={[styles.logoutText, { flex: 1 }]} accessible={false}>
                  Exporting{exportProgress > 0 ? ` ${exportProgress}%` : '...'}
                </Text>
              </View>
            </View>
          ) : (
            <TouchableOpacity
              style={styles.actionRow}
              onPress={handleExportData}
              accessibilityLabel="Export My Data"
              accessibilityRole="button"
              testID="settings.export-my-data-button"
            >
              <Text style={styles.logoutText} accessible={false}>Export My Data</Text>
            </TouchableOpacity>
          )}
          <View style={styles.divider} />
          <TouchableOpacity
            style={styles.actionRow}
            onPress={openAccountPage}
            accessibilityLabel="Manage Your A3RN Account"
            accessibilityHint="Opens your A3RN account: passkeys, signing in, and deleting the A3RN account itself."
            accessibilityRole="link"
            testID="settings.manage-a3rn-account-button"
          >
            <Text style={styles.logoutText} accessible={false}>Manage Your A3RN Account</Text>
          </TouchableOpacity>
          <View style={styles.divider} />
          <TouchableOpacity
            style={styles.actionRow}
            onPress={handleLogout}
            accessibilityLabel="Sign Out"
            accessibilityRole="button"
            testID="settings.sign-out-button"
          >
            <Text style={styles.logoutText} accessible={false}>Sign Out</Text>
          </TouchableOpacity>
          <View style={styles.divider} />
          <TouchableOpacity
            style={styles.actionRow}
            onPress={handleLogoutForget}
            accessibilityLabel="Sign Out & Forget Device"
            accessibilityHint="Removes your encryption key from this device. You will need your recovery phrase to sign in again."
            accessibilityRole="button"
            testID="settings.sign-out-and-forget-device-button"
          >
            <Text style={styles.deleteText} accessible={false}>Sign Out & Forget Device</Text>
          </TouchableOpacity>
          <View style={styles.divider} />
          <TouchableOpacity
            style={styles.actionRow}
            onPress={handleDeleteAccount}
            accessibilityLabel="Delete My Cynosure Data"
            accessibilityHint="Permanently deletes your Cynosure data. Cannot be undone. Your A3RN sign-in stays active."
            accessibilityRole="button"
            // testID deliberately unchanged: it is a stable test handle, not
            // user-facing copy, and renaming it would break the synthetic
            // monitors for no gain.
            testID="settings.delete-account-button"
          >
            <Text style={styles.deleteText} accessible={false}>Delete My Cynosure Data</Text>
          </TouchableOpacity>
        </View>
      </View>

    </ScrollView>
  );
}
